/**
 * The top bar's miniature price line with a brush (spec §7: "one time range,
 * shared by every view, with presets and a brush on a miniature price line in
 * the top bar").
 *
 * Thirty days of hourly candles of the current token job as a sparkline, with
 * the global range drawn as a window over it: drag to set the range, click to
 * centre it on a moment. Nothing is drawn for a non-token job — there is no
 * price line to brush on — so the top bar simply drops the control.
 */
import { useMemo, useRef, useState } from "react";
import { useApi } from "@/api/hooks";
import { qk } from "@/api/keys";
import { paths } from "@/api/client";
import type { CandleSeries, CandlesQuery } from "@/api/types";
import { fmtTime } from "@/lib/format";
import { useCurrentJob } from "@/state/job";
import { useUrlState } from "@/state/useUrlState";

const SPAN = 30 * 86_400;
const BUCKET = 3600;
const W = 160;
const H = 24;

export function MiniBrush() {
  const job = useCurrentJob();
  const { range, setRange } = useUrlState();
  const chain = job.chain ?? "";
  const token = job.token ?? "";
  const enabled = job.kind === "token" && !!chain && !!token;
  // one key per hour: the sparkline is context, not a live readout
  const to = Math.ceil(range.to / BUCKET) * BUCKET;
  const params = useMemo<CandlesQuery>(() => ({ from: to - SPAN, to, bucket: BUCKET }), [to]);
  const q = useApi<CandleSeries>(
    qk.candles(chain, token, params),
    `${paths.job(chain, token)}/candles`,
    { ...params },
    { enabled, staleTime: 5 * 60_000, silent: true },
  );
  const candles = q.data?.data.candles;

  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<{ x0: number; x1: number } | null>(null);

  const path = useMemo(() => {
    if (!candles || candles.length < 2) return null;
    let lo = Infinity;
    let hi = -Infinity;
    for (const c of candles) {
      if (c.close < lo) lo = c.close;
      if (c.close > hi) hi = c.close;
    }
    const span = hi - lo || 1;
    const t0 = params.from!;
    const t1 = params.to!;
    return candles
      .map((c, i) => {
        const x = ((c.t - t0) / (t1 - t0)) * W;
        const y = H - 2 - ((c.close - lo) / span) * (H - 4);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }, [candles, params.from, params.to]);

  if (!enabled) return null;

  const t0 = params.from!;
  const t1 = params.to!;
  const xOf = (t: number) => ((Math.min(t1, Math.max(t0, t)) - t0) / (t1 - t0)) * W;
  const tOf = (x: number) => t0 + (Math.max(0, Math.min(W, x)) / W) * (t1 - t0);
  const selX = xOf(range.from);
  const selW = Math.max(1, xOf(range.to) - selX);

  const localX = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return ((e.clientX - rect.left) / rect.width) * W;
  };

  return (
    <svg
      ref={svgRef}
      className="h-6 w-40 shrink-0 cursor-crosshair rounded-sm border border-border bg-bg-inset"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="slider"
      aria-label="mini price brush: drag to set the time range"
      aria-valuemin={t0}
      aria-valuemax={t1}
      aria-valuenow={range.from}
      aria-valuetext={`${fmtTime(range.from, "MM-dd HH:mm")} → ${fmtTime(range.to, "MM-dd HH:mm")}`}
      tabIndex={0}
      onPointerDown={(e) => {
        const x = localX(e);
        setDrag({ x0: x, x1: x });
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (drag) setDrag({ x0: drag.x0, x1: localX(e) });
      }}
      onPointerUp={(e) => {
        if (!drag) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        const a = Math.min(drag.x0, drag.x1);
        const b = Math.max(drag.x0, drag.x1);
        setDrag(null);
        if (b - a < 3) {
          // a click centres the current span on that moment
          const span = Math.max(BUCKET, range.to - range.from);
          const mid = tOf(a);
          setRange(Math.round(mid - span / 2), Math.round(mid + span / 2));
        } else setRange(Math.round(tOf(a)), Math.round(tOf(b)));
      }}
    >
      <rect x={selX} y={0} width={selW} height={H} className="fill-accent/20" />
      {path && (
        <path
          d={path}
          className="stroke-fg-muted"
          strokeWidth={1}
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {drag && (
        <rect
          x={Math.min(drag.x0, drag.x1)}
          y={0}
          width={Math.max(1, Math.abs(drag.x1 - drag.x0))}
          height={H}
          className="fill-accent/35"
        />
      )}
    </svg>
  );
}
